import React from "react";
import { ApiRetailer, ApiSubwalletBalance } from "@api/interfaces";
import {
  Card,
  CustomerRoleTypeBadge,
  InfoTooltip,
  Text,
  VSpacer,
} from "@/components/DesignSystem";
import { Stack, Tooltip, useTheme } from "@mui/material";
import { calculateAvailablePoints, formatNumber } from "@shared/utilities";
import { AccountGroupRole } from "@shared/enums";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useTranslation } from 'react-i18next';

interface TotalPointsAvailableCardProps {
  accountGroupRole: AccountGroupRole | null | undefined;
  balances: ApiSubwalletBalance[],
  boundaryRef?: React.RefObject<HTMLElement>,
  brandColor: string,
  retailer?: ApiRetailer,
}

export const TotalPointsAvailableCard = ({
  accountGroupRole,
  balances,
  boundaryRef,
  brandColor,
  retailer,
}: TotalPointsAvailableCardProps) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { isMobile } = useMediaQuery();
  const pointsAvailable = calculateAvailablePoints(balances);
  const formattedPointsAvailable = formatNumber(pointsAvailable, i18n);

  const tooltipText = retailer?.hasBookings
    ? t(($) => $.shared.walletPointsAvailableTooltip.text)
    : t(($) => $.shared.walletPointsAvailableTooltip.textWithoutBookings);
  return (
    <Card
      sx={{
        bgcolor: brandColor,
        borderRadius: '12px',
        position: 'relative',
        background: 'linear-gradient(180deg, rgba(0, 0, 0, 0.25) 0.26%, ' +
          `rgba(255, 255, 255, 0.10) 100%), ${brandColor}`,
      }}
      testID="total-points-available-card"
    >
      <Stack alignItems="center" direction="row" justifyContent="space-between">
        <Text
          category="label-medium"
          sx={{
            color: 'white',
            letterSpacing: '0.1px',
            textTransform: 'uppercase',
          }}
        >
          {t(($) => $.wallet.totalPointsAvailable)}
        </Text>
        {isMobile
          ? (
            <InfoTooltip
              backgroundColor={theme.palette.background.paper}
              boundaryRef={boundaryRef}
              iconColor="white"
              openOnClick={true}
            >
              {tooltipText}
            </InfoTooltip>
          )
          : (
            <Tooltip placement="top" title={tooltipText}>
              <InfoOutlinedIcon sx={{
                height: "20px",
                width: "20px",
                cursor: "pointer",
                color: theme.palette.background.paper,
              }} />
            </Tooltip>
          )
        }
      </Stack>
      <Stack flex={1}>
        <VSpacer size="4" />
        <Stack alignItems="center" direction="row" gap="12px">
          <Text
            sx={{
              color: 'white',
              fontSize: '36px',
              fontWeight: 500,
              lineHeight: '44px',
            }}
          >
            {formattedPointsAvailable}
          </Text>
          {!!accountGroupRole && (
            <CustomerRoleTypeBadge
              accountGroupRole={accountGroupRole}
              testID="total-points-available-badge"
            />
          )}
        </Stack>
      </Stack>
    </Card>
  );
};
